import { useCallback, useEffect } from 'react';
import { ChatbotProvider, useChatbot } from '@fff-web/chatbot';
import { useUserContext } from '@fff-web/fff-utilities';
import { fetchUserMetadata } from '../../services';
import isBrowser from '../../utils/isBrowser';
import { type ChatWidgetProps, type DefaultChatWidgetProps } from './ChatWidget.types';
import { flattenUserInfo, flattenUserMetadata, isChatPathAllowed } from './ChatWidget.utils';

const ChatWidgetDefault = ({ hideWidget }: DefaultChatWidgetProps) => {
  const { userInfo } = useUserContext();
  const { isMounted, isOpen, mount, unmount, updateMetadata } = useChatbot();

  /**
   * Manages chatbot mounting state
   */
  useEffect(() => {
    const shouldMount = isBrowser() && isChatPathAllowed() && !hideWidget;

    if (!shouldMount) {
      isMounted && unmount();
      return;
    }

    if (!isMounted) {
      mount({
        userId: userInfo.user.userId?.toString(),
        metadata: { context: flattenUserInfo(userInfo.user) },
      });
    }
  }, [isMounted, hideWidget, mount, unmount, userInfo.user]);

  /**
   * Handle chat open event - ALWAYS fetch fresh metadata on every open
   */
  const handleChatOpen = useCallback(async () => {
    const metaData = await fetchUserMetadata();
    const userMetadata = flattenUserMetadata(metaData);
    updateMetadata({ context: userMetadata });
  }, [updateMetadata]);

  /**
   * Subscribe to chat open event
   */
  useEffect(() => {
    if (!isMounted || !isOpen) return;
    handleChatOpen();
  }, [isMounted, isOpen, handleChatOpen]);

  return null;
};

export const ChatWidget = ({ agentId, ...props }: ChatWidgetProps) => {
  return (
    <ChatbotProvider agentId={agentId}>
      <ChatWidgetDefault {...props} />
    </ChatbotProvider>
  );
};
